import { useFormContext } from 'react-hook-form';
import { useRowsCreatives } from 'features/creatives';
import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocationParams, useSyncGlobalApp } from 'hooks';
import { useCreativeNameFilterUrlSync } from '../hooks';
import { useQueryClient } from '@tanstack/react-query';
import creativesQueryKeys from '../../creatives/api/queryKeys';
import { Box, Chip as MuiChip, Divider, Tooltip, Typography } from '@mui/material';
import {
  AddPackTagButton,
  Checkbox,
  ChipInput,
  CircularProgress,
  PacksWithCreatives,
  SuspenseErrorFallback,
  Switch,
} from 'components/common';
import { DataGrid, type GridRenderCellParams } from '@mui/x-data-grid';
import { SelectAdTypes } from './FacebookFormComponents';
import type { GridRowSelectionModel } from '@mui/x-data-grid/models/gridRowSelectionModel';
import { FormContainer } from 'components/layout';
import type { CreativeEntry, PackEntry } from 'types/shared';
import { useAdTypes } from '../api/getAdTypes';
import { isEmpty } from 'lodash-es';
import InfoIcon from '@mui/icons-material/InfoOutlined';
import { Network } from '../../../constants';
import { HypothesisCreativesList } from './creatives';
import { pruneEmptyPacks } from './creatives/applyPackCreativesMove';
import datadogActions, { DATADOG_EVENT } from 'lib/datadog';
import ScalingEgCreativesForm from './ScalingEgCreativesForm';
import { isScalingEgProcess } from '../utils/marketingProcess';
import { resolveHypothesisCreativesViewState } from '../utils/savedCreativeFilters';

const CREATIVES_LIMIT = 30;

const EMPTY_DEFAULT_PACK = {
  tag: '',
  adType: '',
  creatives: [] as string[],
  filterCreativeByName: [] as string[],
  visual_touch_ups: true,
};

interface CreativesGridProps {
  creativeTab: CreativeTab;
}

const CreativesGrid = ({ creativeTab }: CreativesGridProps) => {
  const { watch, setValue, getValues, trigger } = useFormContext();
  const marketingProcess = watch('marketingProcess') as string;
  const isHypothesisTestingProcess = marketingProcess === 'hypothesis_testing';

  const { rows, isLoading, isError, error } = useRowsCreatives({
    params: {
      network: Network.FACEBOOK,
      ...(isHypothesisTestingProcess
        ? { forceEnabled: true, with_hypothesis: true }
        : {}),
    },
  });

  const platform = watch('platform') as string;
  const selectedCreatives = watch('selectedCreatives') as string[];
  const selectedOnly = watch('selectedCreativesOnly') as boolean;
  const packsWithCreatives = watch('packsWithCreatives') as PackEntry[];
  const activePacksWithCreatives = watch('activePacksWithCreatives') as number;
  const adType = packsWithCreatives?.[activePacksWithCreatives]?.adType ?? '';
  const isDLO = adType?.toLowerCase() === 'dlo';

  const { data: adTypes } = useAdTypes({
    params: { platform },
    config: {
      enabled: !!platform,
    },
  });

  const COLUMNS = useMemo(() => {
    const currentAdTypeValue =
      adTypes?.find((i) => i.code === adType)?.name ?? adType;

    return [
      { field: 'creative_id', headerName: 'Creative ID', width: 120 },
      {
        field: 'name',
        headerName: 'Name',
        width: 450,
        renderCell: (params: GridRenderCellParams<CreativeEntry>) => {
          const isSelectable =
            (isDLO && !isEmpty(params.row.locales)) || !isDLO;
          const tooltipMessage = !isSelectable
            ? `Can't be used for ${currentAdTypeValue} without localizations`
            : null;

          return (
            <>
              <Tooltip title={tooltipMessage} arrow placement={'top'}>
                <Box
                  sx={{
                    display: 'flex',
                    gap: 0.25,
                    height: '100%',
                    flexDirection: 'column',
                    justifyContent: 'center',
                  }}
                >
                  <Typography variant={'body2'}>{params.row.name}</Typography>
                  <Typography
                    variant={'caption'}
                    sx={(t) => ({ color: t.palette.text.disabled })}
                  >
                    {isSelectable && !isEmpty(params.row.locales)
                      ? `Loc: ${params.row.locales?.join(', ')}`
                      : 'No localizations'}
                  </Typography>
                </Box>
              </Tooltip>
            </>
          );
        },
      },
      { field: 'app', headerName: 'App', width: 120 },
      { field: 'creative_type', headerName: 'Type', width: 120 },
      { field: 'created', headerName: 'Created', width: 120 },
    ];
  }, [adType, adTypes, isDLO]);

  const packGridSelectedIds = useMemo((): string[] => {
    const ids =
      !isHypothesisTestingProcess || activePacksWithCreatives == null
        ? selectedCreatives ?? []
        : packsWithCreatives[activePacksWithCreatives]?.creatives ?? [];
    return ids.filter((id): id is string => typeof id === 'string');
  }, [
    activePacksWithCreatives,
    isHypothesisTestingProcess,
    packsWithCreatives,
    selectedCreatives,
  ]);

  const filteredRows = useMemo(() => {
    if (!selectedOnly) return rows;

    return rows?.filter((row) => packGridSelectedIds.includes(row.id));
  }, [rows, packGridSelectedIds, selectedOnly]);

  const rowSelectionModel = useMemo(() => {
    const availableIds = new Set((filteredRows ?? []).map((row) => row.id));
    return packGridSelectedIds.filter((id) => availableIds.has(id));
  }, [filteredRows, packGridSelectedIds]);

  const applySelection = useCallback(
    (ids: string[]) => {
      const currentPacks = getValues('packsWithCreatives') as PackEntry[];
      const activeIndex = getValues('activePacksWithCreatives') as number;

      setValue(
        'packsWithCreatives',
        currentPacks.map((pack, index) =>
          index === activeIndex ? { ...pack, creatives: ids } : pack,
        ),
        { shouldValidate: true },
      );
      if (!isHypothesisTestingProcess) {
        setValue('selectedCreatives', ids, { shouldValidate: true });
      }
      void trigger();
    },
    [getValues, isHypothesisTestingProcess, setValue, trigger],
  );

  const onSelectionModelChange = useCallback(
    (selectedRows: GridRowSelectionModel) => {
      const parsedSelectedRows = selectedRows.slice(0, CREATIVES_LIMIT).map(String);

      applySelection(parsedSelectedRows);
      datadogActions.trackDatadogAction(DATADOG_EVENT.creativeSelected, {
        selectedCount: parsedSelectedRows.length,
        totalRowsCount: filteredRows?.length ?? 0,
        creativeTab,
        adType: adType || undefined,
      });
    },
    [adType, applySelection, creativeTab, filteredRows],
  );

  useEffect(() => {
    void trigger('packsWithCreatives');
    void trigger('selectedCreatives');
  }, [trigger]);

  if (isError) {
    return (
      <SuspenseErrorFallback
        title={'Choose Creatives'}
        message={
          (
            error as {
              cause?: { error?: string };
            }
          )?.cause?.error ?? 'An error occurred while fetching creatives'
        }
      />
    );
  }

  if (isLoading && isEmpty(filteredRows)) {
    return <CircularProgress title={'Creatives are loading...'} />;
  }

  if (isEmpty(filteredRows)) {
    return (
      <Typography
        data-testid="noCreativesPlaceholder"
        sx={(t) => ({
          color: t.palette.grey[400],
          fontSize: t.typography.h4.fontSize,
        })}
      >
        No creatives found
      </Typography>
    );
  }

  return (
    <Box style={{ position: 'relative', height: '100%', width: '100%' }}>
      {isLoading && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            display: 'flex',
            alignContent: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100%',
            zIndex: 2,
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
          }}
        >
          <CircularProgress title={'Creatives are loading...'} />
        </Box>
      )}
      <DataGrid
        rows={filteredRows ?? []}
        columns={COLUMNS}
        localeText={{
          noRowsLabel: !adType && !isHypothesisTestingProcess ? 'Select Ad type to add creatives' : '',
        }}
        checkboxSelection
        rowSelectionModel={rowSelectionModel}
        onRowSelectionModelChange={onSelectionModelChange}
        keepNonExistentRowsSelected={!isHypothesisTestingProcess}
        disableVirtualization
        hideFooterPagination
        getRowId={(row) => row.id}
        isRowSelectable={(params) => {
          const isDLOLocalesEmpty = isDLO && !isEmpty(params.row.locales);
          const isUpToLimit =
            packGridSelectedIds.length < CREATIVES_LIMIT ||
            packGridSelectedIds.includes(params.row.id);
          return isDLOLocalesEmpty || !isDLO || isUpToLimit;
        }}
        slots={{
          footer: () =>
            rows?.length ? (
              <Box
                sx={(t) => ({
                  p: 2,
                  height: 54,
                  minHeight: 54,
                  display: 'flex',
                  alignItems: 'center',
                  gap: .5,
                  borderTop: `1px solid ${t.palette.divider}`,
                  cursor: 'default',
                })}
              >
                <Typography variant={'body2'}>
                  {rows?.length} found /
                </Typography>
                {packGridSelectedIds.length === CREATIVES_LIMIT ? (
                  <>
                    <InfoIcon color={'primary'} sx={{ fontSize: 21 }} />
                    <Typography
                      variant={'body2'}
                    >{`Up to ${CREATIVES_LIMIT} creatives can be selected`}</Typography>
                  </>
                ) : (
                  <Typography variant={'body2'}>{packGridSelectedIds.length} selected</Typography>
                )}
              </Box>
            ) : null,
        }}
      />
    </Box>
  );
};

type CreativeTab = 'test_creatives' | 'pack';

const CreativesForm: FC = () => {
  const { watch, setValue, getValues } = useFormContext();
  const queryClient = useQueryClient();
  const {
    adType: adTypeParam,
    setCreativesByPack: setCreativesByPackParam,
  } = useLocationParams();

  const handleAppChange = useCallback(() => {
    setValue('selectedCampaigns', [], { shouldValidate: true });
    setValue('selectedAdSets', [], { shouldValidate: true });
    setValue('selectedCreatives', [], { shouldValidate: true });
    if (isScalingEgProcess(getValues('marketingProcess') as string)) {
      setValue('packsWithCreatives', [], { shouldValidate: true });
      setValue('activePacksWithCreatives', null, { shouldValidate: true });
    } else {
      setValue(
        'packsWithCreatives',
        [{ ...EMPTY_DEFAULT_PACK }],
        { shouldValidate: true },
      );
      setValue('activePacksWithCreatives', 0, { shouldValidate: true });
    }
    setValue(
      'testCreativesViewState',
      resolveHypothesisCreativesViewState(getValues('app') as string | undefined),
    );
  }, [getValues, setValue]);

  useSyncGlobalApp(handleAppChange);

  const packsWithCreatives = watch('packsWithCreatives') as PackEntry[];
  const activePacksWithCreatives = watch('activePacksWithCreatives') as number | null;
  const filterCreativeByName = watch(
    `packsWithCreatives.${activePacksWithCreatives ?? 0}.filterCreativeByName`,
  ) as string[] | undefined;
  useCreativeNameFilterUrlSync(filterCreativeByName ?? []);
  const marketingProcess = watch('marketingProcess') as string;
  const isMarketingProcessInbox = marketingProcess?.includes('inbox');
  const isMarketingProcessScalingEg = isScalingEgProcess(marketingProcess);
  const isMarketingProcessHypothesisTesting = marketingProcess === 'hypothesis_testing';
  const adType = packsWithCreatives?.[activePacksWithCreatives ?? 0]?.adType ?? '';

  const prevActivePackIndexRef = useRef(activePacksWithCreatives);
  const lastSeenAdTypeParamRef = useRef(adTypeParam);
  if (prevActivePackIndexRef.current !== activePacksWithCreatives) {
    prevActivePackIndexRef.current = activePacksWithCreatives;
    lastSeenAdTypeParamRef.current = adTypeParam;
  }

  useEffect(() => {
    if (isMarketingProcessScalingEg) {
      lastSeenAdTypeParamRef.current = adTypeParam;
      return;
    }
    if (!adTypeParam || adTypeParam === adType) {
      lastSeenAdTypeParamRef.current = adTypeParam;
      return;
    }
    if (adTypeParam === lastSeenAdTypeParamRef.current) return;
    lastSeenAdTypeParamRef.current = adTypeParam;

    setValue(`packsWithCreatives.${activePacksWithCreatives ?? 0}.adType`, adTypeParam, {
      shouldValidate: true,
    });
    setValue('adType', adTypeParam, { shouldValidate: true });
  }, [activePacksWithCreatives, adType, adTypeParam, isMarketingProcessScalingEg, setValue]);

  const testCreativesTab = watch('testCreativesTab') as CreativeTab | undefined;

  const packVisualTouchUpsKey = packsWithCreatives
    .map((pack) => (pack.visual_touch_ups ? '1' : '0'))
    .join('');

  // Persists the full pack structure to the URL, grouped by pack (keyed by
  // tag when set — this is also the hypothesis text for Hypothesis Testing —
  // or by index otherwise), so it self-heals after any mutation (add/delete/
  // split/merge pack, rename, drag and drop, ...) regardless of which of
  // those call sites triggered it. Scaling EG owns the same `creatives`
  // param exclusively (flat format) via its own form, since it has no packs.
  // `packVisualTouchUpsKey` is required because toggling Visual touch-ups
  // updates a nested field without replacing the packs array.
  useEffect(() => {
    if (isMarketingProcessScalingEg) return;

    setCreativesByPackParam(
      packsWithCreatives.map((pack) => ({
        tag: pack.tag,
        creatives: pack.creatives,
        visualTouchUps: pack.visual_touch_ups,
      })),
    );
  }, [
    isMarketingProcessScalingEg,
    packVisualTouchUpsKey,
    packsWithCreatives,
    setCreativesByPackParam,
  ]);

  const [activeTab, setActiveTab] = useState<CreativeTab>(
    isMarketingProcessHypothesisTesting ? 'test_creatives' : 'pack',
  );

  const setTab = useCallback((tab: CreativeTab) => {
    setActiveTab(tab);
    setValue('testCreativesTab', tab);
  }, [setValue]);

  useEffect(() => {
    if (
      isMarketingProcessHypothesisTesting &&
      testCreativesTab &&
      testCreativesTab !== activeTab
    ) {
      setActiveTab(testCreativesTab);
    }
  }, [isMarketingProcessHypothesisTesting, testCreativesTab, activeTab]);

  useEffect(() => {
    setTab(isMarketingProcessHypothesisTesting ? 'test_creatives' : 'pack');
  }, [isMarketingProcessHypothesisTesting, setTab]);

  // Switch back to "All test creatives" when the last pack is deleted.
  useEffect(() => {
    if (isMarketingProcessHypothesisTesting && packsWithCreatives.length === 0) {
      setTab('test_creatives');
    }
  }, [isMarketingProcessHypothesisTesting, packsWithCreatives.length, setTab]);

  const handlePackDeleted = useCallback(() => {
    if (isMarketingProcessHypothesisTesting) {
      void queryClient.invalidateQueries({ queryKey: creativesQueryKeys.allCreativesWithHypothesis });
    }
  }, [isMarketingProcessHypothesisTesting, queryClient]);

  const handlePackUndone = useCallback(() => {
    if (isMarketingProcessHypothesisTesting) {
      setTab('pack');
      void queryClient.invalidateQueries({ queryKey: creativesQueryKeys.allCreativesWithHypothesis });
    }
  }, [isMarketingProcessHypothesisTesting, queryClient, setTab]);

  const handleAllHypothesisCreativesClick = useCallback(() => {
    const { packs: prunedPacks, removedIndices } = pruneEmptyPacks(packsWithCreatives);

    if (removedIndices.length > 0) {
      setValue('packsWithCreatives', prunedPacks, { shouldValidate: true });
      void queryClient.invalidateQueries({ queryKey: creativesQueryKeys.allCreativesWithHypothesis });
    }

    setTab('test_creatives');
    setValue('activePacksWithCreatives', null);
  }, [packsWithCreatives, queryClient, setTab, setValue]);

  useEffect(() => {
    if (isMarketingProcessHypothesisTesting || isMarketingProcessScalingEg) {
      return;
    }

    if (marketingProcess && packsWithCreatives.length === 0) {
      setValue('packsWithCreatives', [
        {
          tag: '',
          adType: '',
          creatives: [],
          filterCreativeByName: [],
          visual_touch_ups: true,
        },
      ]);
      setValue('activePacksWithCreatives', 0);
    }
  }, [isMarketingProcessHypothesisTesting, isMarketingProcessScalingEg, marketingProcess, packsWithCreatives, setValue]);

  if (isMarketingProcessScalingEg) {
    return <ScalingEgCreativesForm />;
  }

  const showHypothesisCreativesList =
    activeTab === 'test_creatives' && isMarketingProcessHypothesisTesting;

  return (
    <FormContainer
      title={isMarketingProcessHypothesisTesting || adType ? 'Choose Creatives' : 'Select Ad type'}
      sx={{ flexDirection: 'column', gap: 2 }}
    >
      <Box
        sx={{ display: 'flex', flex: 1, flexDirection: 'column', gap: 2, width: '100%' }}
      >
        {/* Tab row: "All test creatives" chip + Pack chips (only for testing) */}
        {isMarketingProcessHypothesisTesting ? (
          <Box
            sx={{ display: 'flex', flexDirection: 'row', gap: 1, width: '100%', alignItems: 'center', flexWrap: 'wrap' }}
          >
            <MuiChip
              label="All test creatives"
              color={activeTab === 'test_creatives' ? 'primary' : 'default'}
              onClick={handleAllHypothesisCreativesClick}
            />
            {packsWithCreatives.length > 0 && (
              <>
                <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                <Box
                  onClick={() => { setTab('pack'); }}
                  sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}
                >
                  <PacksWithCreatives
                    highlightActive={activeTab === 'pack'}
                    allowDeleteLast
                    onAfterDelete={handlePackDeleted}
                    onAfterUndo={handlePackUndone}
                  />
                </Box>
              </>
            )}
          </Box>
        ) : (
          <Box
            sx={{ display: 'flex', flexDirection: 'row', gap: 2, width: '100%' }}
          >
            <PacksWithCreatives />
          </Box>
        )}

        {/* Controls for pack view (Ad type selector, name filter, etc.) */}
        {!showHypothesisCreativesList && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              gap: 2,
              width: '100%',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                flex: 2,
                flexDirection: 'row',
                alignItems: 'start',
                flexWrap: 'nowrap',
              }}
            >
              {!isMarketingProcessHypothesisTesting && (
                <Box sx={{ maxWidth: '140px', }}>
                  <SelectAdTypes key={activePacksWithCreatives} />
                </Box>
              )}
              <Box sx={{flex: 2}}>
                <ChipInput
                  name={`packsWithCreatives.${activePacksWithCreatives}.filterCreativeByName`}
                  inputLabel="Enter creative name"
                />
              </Box>
              <Checkbox
                dataTestId={'selectedCreativesOnly'}
                name={'selectedCreativesOnly'}
                label={'Selected only'}
                controlLabelSx={{ whiteSpace: 'nowrap' }}
              />
            </Box>
            {!isMarketingProcessHypothesisTesting && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'start',
                  justifyContent: 'end',
                  flex: 1,
                  gap: 2,
                }}
              >
                {!isMarketingProcessInbox && (
                  <>
                    <Divider
                      sx={{ mx: 1, maxHeight: 32 }}
                      orientation="vertical"
                      variant="middle"
                      flexItem
                    />
                    <Box sx={{alignItems: 'center', minHeight: 38, display: 'flex', flexWrap: 'nowrap', gap: 3}}>
                      <Switch
                        sx={{ m: 0, p: 0 }}
                        key={`packsWithCreatives.${activePacksWithCreatives}.visual_touch_ups`}
                        name={`packsWithCreatives.${activePacksWithCreatives}.visual_touch_ups`}
                        label={'Visual touch-ups'}
                      />
                      <AddPackTagButton />
                    </Box>
                  </>
                )}
              </Box>
            )}
          </Box>
        )}

        {isMarketingProcessHypothesisTesting ? (
          <>
            <Box sx={{ display: showHypothesisCreativesList ? 'block' : 'none', width: '100%' }}>
              <HypothesisCreativesList />
            </Box>
            {!showHypothesisCreativesList && (
              <Box sx={{flex: 1, height: '100%', width: '100%', minHeight: 400, position: 'relative' }}>
                <Box sx={{position: 'absolute', overflowY: 'auto', width: '100%', height: '100%'}}>
                  <CreativesGrid creativeTab={'pack'} />
                </Box>
              </Box>
            )}
          </>
        ) : (
          <Box sx={{flex: 1, height: '100%', width: '100%', minHeight: 400, position: 'relative' }}>
            <Box sx={{position: 'absolute', overflowY: 'auto', width: '100%', height: '100%'}}>
              <CreativesGrid creativeTab={'pack'} />
            </Box>
          </Box>
        )}
      </Box>
    </FormContainer>
  );
};

export default CreativesForm;
